import { observer } from "mobx-react";
import React from "react";
import { useInjection } from "inversify-react";
import { useBetween } from "use-between";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import DropDeskIcon from "@dropDesk/storage/icons";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import { hexToRGBA } from "@dropDesk/utils/helpers/colors";
import { SharedColors } from "@dropDesk/domain/entities/theme/app_colors";
import AvatarImage from "@dropDesk/presentation/components/avatar";
import {
    FeaturedAttendant,
    FeaturedAttendantHighlight,
    FeaturedAttendantStatus,
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/featured_attendant.entity";
import {
    useModalsReportTicketComparative
} from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/modals/handle_change_visible";
import {
    AttendantInfo,
    AttendantName,
    AttendantRow,
    AttendantsCard,
    AttendantsHead,
    AttendantsHeader,
    AttendantsNote,
    AttendantsTable,
    AttendantsTitle,
    EmptyState,
    HeadCell,
    HighlightRow,
    HighlightText,
    InfoButton,
    RatingCell,
    RatingValue,
    SkeletonBone,
    TicketCount,
    TrendPill,
} from "./styled";

const formatRating = (value: number): string => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const getTrendLabel = (
    status: FeaturedAttendantStatus,
    highlight?: FeaturedAttendantHighlight,
): string => {
    if (highlight === 'highest_volume') {
        return 'Maior volume';
    }
    if (highlight === 'best_rating') {
        return 'Melhor avaliação';
    }
    if (highlight === 'best_response_time') {
        return 'Melhor resposta';
    }
    if (highlight === 'highest_evolution') {
        return 'Maior evolução';
    }
    if (highlight === 'best_overall') {
        return 'Melhor no geral';
    }
    if (status === 'rising') {
        return 'Em alta';
    }
    if (status === 'falling') {
        return 'Em baixa';
    }
    if (status === 'insufficient_data') {
        return 'Dados insuficientes';
    }
    return 'Estável';
};

const getTrendTone = (
    status: FeaturedAttendantStatus,
    highlight: FeaturedAttendantHighlight | undefined,
    hint: string,
): { color: string; background: string; icon: string | null; rotateUp?: boolean } => {
    if (highlight === 'highest_volume') {
        return {
            color: SharedColors.warning,
            background: hexToRGBA(SharedColors.warning, 0.15),
            icon: ICONS_DROPDESK.dashboard,
        };
    }
    if (highlight === 'best_overall' || highlight === 'best_rating') {
        return {
            color: SharedColors.success,
            background: hexToRGBA(SharedColors.success, 0.15),
            icon: ICONS_DROPDESK.star,
        };
    }
    if (status === 'rising') {
        return {
            color: SharedColors.success,
            background: hexToRGBA(SharedColors.success, 0.15),
            icon: ICONS_DROPDESK.arrowUp,
        };
    }
    if (status === 'falling') {
        return {
            color: SharedColors.errorOnPrimary,
            background: hexToRGBA(SharedColors.errorOnPrimary, 0.15),
            icon: ICONS_DROPDESK.arrowDown,
        };
    }
    return {
        color: hint,
        background: hexToRGBA(hint, 0.15),
        icon: null,
    };
};

const FeaturedAttendants = observer(({
    attendants,
    highlight,
    loading = false,
}: {
    attendants: FeaturedAttendant[];
    highlight?: string;
    loading?: boolean;
}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const softBorder = hexToRGBA(colors.border, 0.7);
    const skeletonBackground = hexToRGBA(colors.border, 0.55);
    const skeletonHighlight = hexToRGBA(colors.hint, 0.35);
    const {
        setVisibleModalInformationReport,
        setTextHeaderModalInformationReport,
        setTextInformationHeader,
        setTextInformationBody,
    } = useBetween(useModalsReportTicketComparative);

    const openInformationModal = () => {
        setTextHeaderModalInformationReport('Atendentes em destaque');
        setTextInformationHeader(
            'Classificamos cada atendente pela evolução da satisfação, do volume e dos tempos de resposta e solução entre os períodos.'
        );
        setTextInformationBody(
            'Os destaques apontam quem se saiu melhor em cada critério.'
        );
        setVisibleModalInformationReport(true);
    };

    return (
        <AttendantsCard background={colors.onBackground} border={colors.border}>
            <AttendantsHeader>
                <AttendantsTitle color={colors.text}>Atendentes em destaque</AttendantsTitle>
                {!loading && (
                    <InfoButton
                        background={colors.buttonOnBackground}
                        accent={colors.accent}
                        onClick={openInformationModal}
                    >
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.info}
                            size={16}
                            color={colors.text}
                            style={{ display: 'flex' }}
                        />
                    </InfoButton>
                )}
            </AttendantsHeader>

            {!loading && attendants.length === 0 && (
                <EmptyState color={colors.hint}>Sem dados para o período selecionado.</EmptyState>
            )}

            {(loading || attendants.length > 0) && (
                <AttendantsTable>
                    <AttendantsHead border={softBorder}>
                        <HeadCell color={colors.hint}>Atendente</HeadCell>
                        <HeadCell color={colors.hint}>Avaliação</HeadCell>
                        <HeadCell color={colors.hint}>Atendimentos</HeadCell>
                        <HeadCell color={colors.hint}>Tendência</HeadCell>
                    </AttendantsHead>
                    {loading && Array.from({length: 5}).map((_, index) => (
                        <AttendantRow key={index} border={softBorder}>
                            <AttendantInfo>
                                <SkeletonBone
                                    background={skeletonBackground}
                                    highlight={skeletonHighlight}
                                    width="36px"
                                    height="36px"
                                    borderRadius="50%"
                                />
                                <SkeletonBone
                                    background={skeletonBackground}
                                    highlight={skeletonHighlight}
                                    width={`${68 - (index * 5)}%`}
                                    height="13px"
                                />
                            </AttendantInfo>
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="46px"
                                height="13px"
                            />
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="38px"
                                height="13px"
                            />
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="96px"
                                height="28px"
                                borderRadius="16px"
                            />
                        </AttendantRow>
                    ))}
                    {!loading && attendants.map((attendant) => {
                        const trendTone = getTrendTone(
                            attendant.status,
                            attendant.highlight,
                            colors.hint,
                        );
                        const label = getTrendLabel(attendant.status, attendant.highlight);

                        return (
                            <AttendantRow key={attendant.id} border={softBorder}>
                                <AttendantInfo>
                                    <AvatarImage
                                        title={attendant.name}
                                        url={attendant.urlImageProfile}
                                        size={36}
                                        round={true}
                                        useCursorPointer={!!attendant.urlImageProfile}
                                        hasPreviewImage={!!attendant.urlImageProfile}
                                        withBorder={true}
                                    />
                                    <AttendantName color={colors.text} title={attendant.name}>
                                        {attendant.name}
                                    </AttendantName>
                                </AttendantInfo>
                                <RatingCell>
                                    <DropDeskIcon icon={ICONS_DROPDESK.star} size={14} color={SharedColors.rate} />
                                    <RatingValue color={colors.text}>{formatRating(attendant.rating)}</RatingValue>
                                </RatingCell>
                                <TicketCount color={colors.text}>
                                    {Math.round(attendant.ticketCount).toLocaleString('pt-BR')}
                                </TicketCount>
                                <TrendPill color={trendTone.color} background={trendTone.background}>
                                    {trendTone.icon ? (
                                        <DropDeskIcon
                                            icon={trendTone.icon}
                                            size={12}
                                            color={trendTone.color}
                                        />
                                    ) : (
                                        <span style={{ lineHeight: 1 }}>—</span>
                                    )}
                                    {label}
                                </TrendPill>
                            </AttendantRow>
                        );
                    })}
                </AttendantsTable>
            )}

            {!!highlight && !loading && (
                <HighlightRow
                    border={softBorder}
                    background={'transparent'}
                >
                    <DropDeskIcon icon={ICONS_DROPDESK.check} size={14} color={SharedColors.success} />
                    <HighlightText color={colors.hintInverse}>{highlight}</HighlightText>
                </HighlightRow>
            )}

        </AttendantsCard>
    );
});

export default FeaturedAttendants;
